import React from 'react';
import { X, Mail, Phone, MapPin, Building2, Users as UsersIcon, Inbox, Paperclip, FileText, Receipt, Calendar } from 'lucide-react';
import { Contact } from '../types';
import { useTranslation } from '../i18n/hooks/useTranslation';
import { formatDate } from '../utils/dateUtils';

interface ContactViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  contact: Contact | null;
  emailCounts: { [key: string]: number };
  attachmentCounts: { [key: string]: number };
  invoiceCounts: { [key: string]: number };
  billCounts: { [key: string]: number };
  onViewEmails?: () => void;
  onViewPeople?: () => void;
  onViewInvoices?: () => void;
  onViewBills?: () => void;
}

export const ContactViewModal: React.FC<ContactViewModalProps> = ({
  isOpen,
  onClose,
  contact,
  emailCounts,
  attachmentCounts,
  invoiceCounts,
  billCounts,
  onViewEmails,
  onViewPeople,
  onViewInvoices,
  onViewBills
}) => {
  const { t } = useTranslation();

  if (!isOpen || !contact) return null;

  const formatDateLocal = (dateString: string | Date | undefined) => {
    if (!dateString) return '—';
    return formatDate(dateString);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-600">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {contact.name}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 border-b border-gray-200 dark:border-gray-600 pb-2">
                {t('contact.basicInfo')}
              </h3>
              
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <Building2 className="w-5 h-5 text-gray-500 dark:text-gray-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('contact.type')}</div>
                    <div className="text-gray-900 dark:text-gray-100">
                      {contact.isClient && contact.isSupplier ? t('contact.both') :
                       contact.isClient ? t('contact.client') :
                       contact.isSupplier ? t('contact.supplier') :
                       t('contact.contact')}
                    </div>
                  </div>
                </div>

                {contact.email && (
                  <div className="flex items-start gap-3">
                    <Mail className="w-5 h-5 text-gray-500 dark:text-gray-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('contact.email')}</div>
                      <div className="text-gray-900 dark:text-gray-100">{contact.email}</div>
                    </div>
                  </div>
                )}

                {contact.phone && (
                  <div className="flex items-start gap-3">
                    <Phone className="w-5 h-5 text-gray-500 dark:text-gray-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('contact.phone')}</div>
                      <div className="text-gray-900 dark:text-gray-100">{contact.phone}</div>
                    </div>
                  </div>
                )}

                {contact.address?.formatted && (
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-gray-500 dark:text-gray-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('contact.address')}</div>
                      <div className="text-gray-900 dark:text-gray-100">{contact.address.formatted}</div>
                    </div>
                  </div>
                )}

                {contact.createdAt && (
                  <div className="flex items-start gap-3">
                    <Calendar className="w-5 h-5 text-gray-500 dark:text-gray-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('contact.createdAt')}</div>
                      <div className="text-gray-900 dark:text-gray-100">{formatDateLocal(contact.createdAt)}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Statistics */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 border-b border-gray-200 dark:border-gray-600 pb-2">
                {t('contact.statistics')}
              </h3>
              
              <div className="space-y-3">
                {contact.contactPersons && contact.contactPersons.length > 0 && (
                  <button
                    onClick={onViewPeople}
                    className="w-full flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors text-left"
                  >
                    <div className="flex items-center gap-3">
                      <UsersIcon className="w-5 h-5 text-success-600 dark:text-success-400" />
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{t('contact.people')}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{contact.contactPersons.length} {contact.contactPersons.length === 1 ? t('contact.person') : t('contact.people')}</div>
                      </div>
                    </div>
                    <span className="text-lg font-semibold text-success-600 dark:text-success-400">{contact.contactPersons.length}</span>
                  </button>
                )}

                {emailCounts[contact._id] > 0 && (
                  <button
                    onClick={onViewEmails}
                    className="w-full flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors text-left"
                  >
                    <div className="flex items-center gap-3">
                      <Inbox className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{t('contact.threads')}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{emailCounts[contact._id]} {emailCounts[contact._id] === 1 ? t('contact.emailThread') : t('contact.emailThreads')}</div>
                      </div>
                    </div>
                    <span className="text-lg font-semibold text-primary-600 dark:text-primary-400">{emailCounts[contact._id]}</span>
                  </button>
                )}

                {attachmentCounts[contact._id] > 0 && (
                  <div className="w-full flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Paperclip className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{t('contact.attachments')}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{attachmentCounts[contact._id]} {attachmentCounts[contact._id] === 1 ? 'attachment' : 'attachments'}</div>
                      </div>
                    </div>
                    <span className="text-lg font-semibold text-purple-600 dark:text-purple-400">{attachmentCounts[contact._id]}</span>
                  </div>
                )}

                {contact.isClient && invoiceCounts[contact._id] > 0 && (
                  <button
                    onClick={onViewInvoices}
                    className="w-full flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors text-left"
                  >
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-accent-600 dark:text-accent-400" />
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{t('contact.invoices')}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{invoiceCounts[contact._id]} {invoiceCounts[contact._id] === 1 ? t('contact.invoiceSingular') : t('contact.invoicePlural')}</div>
                      </div>
                    </div>
                    <span className="text-lg font-semibold text-accent-600 dark:text-accent-400">{invoiceCounts[contact._id]}</span>
                  </button>
                )}

                {contact.isSupplier && billCounts[contact._id] > 0 && (
                  <button
                    onClick={onViewBills}
                    className="w-full flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors text-left"
                  >
                    <div className="flex items-center gap-3">
                      <Receipt className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{t('contact.bills')}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{billCounts[contact._id]} {billCounts[contact._id] === 1 ? 'bill' : 'bills'}</div>
                      </div>
                    </div>
                    <span className="text-lg font-semibold text-purple-600 dark:text-purple-400">{billCounts[contact._id]}</span>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* People List */}
          {contact.contactPersons && contact.contactPersons.length > 0 && (
            <div className="mt-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 border-b border-gray-200 dark:border-gray-600 pb-2 mb-4">
                {t('contact.people')}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {contact.contactPersons.map((person, index) => (
                  <div
                    key={index}
                    className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600"
                  >
                    <div className="font-medium text-gray-900 dark:text-gray-100">
                      {person.firstName} {person.lastName}
                    </div>
                    {person.email && (
                      <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        <Mail className="w-3 h-3 inline mr-1" />
                        {person.email}
                      </div>
                    )}
                    {person.phone && (
                      <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        <Phone className="w-3 h-3 inline mr-1" />
                        {person.phone}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-600">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>
  );
};

